import { Injectable, ElementRef } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class ProjectHubTruncateService {
  private readonly FONT_SIZE = 14; // Font size in pixels
  private readonly LINE_HEIGHT = 1.5; // Line height multiplier
  private readonly MAX_LINES = 3; // Maximum number of lines

  /**
   * Truncates content based on container width, handling text and HTML with images
   * @param container ElementRef of the container
   * @param content The content to truncate (text or HTML string)
   * @returns Object with truncated content and boolean indicating if truncation occurred
   */
  truncateContent(
    container: ElementRef,
    content: string
  ): { content: string; wasTruncated: boolean } {
    if (!container || !content) return { content, wasTruncated: false };

    const containerWidth = container.nativeElement.offsetWidth;
    if (!containerWidth) return { content, wasTruncated: false };

    // Sanitize content to remove font-size and alignment styles
    

    // Check if content needs truncation
    const totalLines = this.estimateLines(containerWidth, content);
    if (totalLines <= this.MAX_LINES) {
      return { content: content, wasTruncated: false };
    }

    // Create a temporary container for measurement
    const tempContainer = document.createElement("div");
    tempContainer.style.position = "absolute";
    tempContainer.style.visibility = "hidden";
    tempContainer.style.width = `${containerWidth}px`;
    tempContainer.style.fontSize = `${this.FONT_SIZE}px`;
    tempContainer.style.lineHeight = `${this.LINE_HEIGHT}`;
    tempContainer.style.wordWrap = "break-word";
    document.body.appendChild(tempContainer);

    // Parse HTML content
    tempContainer.innerHTML = content;

    // Count lines for images and paragraphs
    let currentLines = 0;
    const nodes = Array.from(tempContainer.childNodes);
    let truncatedContent = "";
    let wasTruncated = false;

    for (const node of nodes) {
      if (currentLines >= this.MAX_LINES) {
        wasTruncated = true;
        break;
      }

      if (node.nodeName === "IMG") {
        // Images count as 2 lines
        if (currentLines + 2 <= this.MAX_LINES) {
          truncatedContent += (node as HTMLElement).outerHTML || "";
          currentLines += 2;
        } else {
          wasTruncated = true;
          break;
        }
      } else if (node.nodeName === "TABLE") {
        // Tables count based on number of rows (thead + tbody rows)
        const table = node as HTMLElement;
        const tableLines = this.calculateTableLines(table);

        if (currentLines + tableLines <= this.MAX_LINES) {
          truncatedContent += table.outerHTML || "";
          currentLines += tableLines;
        } else {
          // Entire table doesn't fit, exclude it
          wasTruncated = true;
          break;
        }
      } else if (node.nodeName === "P") {
        const paragraph = node as HTMLElement;
        const hasImage = paragraph.querySelector("img") !== null;
        const hasTable = paragraph.querySelector("table") !== null;
        const text = paragraph.textContent || "";

        if (hasImage) {
          // Paragraph with image counts as 2 lines
          if (currentLines + 2 <= this.MAX_LINES) {
            truncatedContent += paragraph.outerHTML;
            currentLines += 2;
          } else {
            wasTruncated = true;
            break;
          }
        } else if (hasTable) {
          // Paragraph with table counts based on number of rows
          const table = paragraph.querySelector("table") as HTMLElement;
          const tableLines = this.calculateTableLines(table);

          if (currentLines + tableLines <= this.MAX_LINES) {
            truncatedContent += paragraph.outerHTML;
            currentLines += tableLines;
          } else {
            // Entire paragraph with table doesn't fit, exclude it
            wasTruncated = true;
            break;
          }
        } else if (!text.trim()) {
          // Handle empty paragraphs (e.g., <p><br></p>) as 1 line
          if (currentLines + 1 <= this.MAX_LINES) {
            truncatedContent += paragraph.outerHTML;
            currentLines += 1;
          } else {
            wasTruncated = true;
            break;
          }
        } else {
          // Handle text paragraph - preserve inner HTML formatting
          const paragraphHTML = paragraph.innerHTML;
          let tempPara = document.createElement("p");
          tempPara.innerHTML = paragraphHTML;
          tempContainer.appendChild(tempPara);

          const height = tempPara.offsetHeight;
          const lines = Math.ceil(height / (this.FONT_SIZE * this.LINE_HEIGHT));

          if (currentLines + lines <= this.MAX_LINES) {
            // Paragraph fits completely
            truncatedContent += paragraph.outerHTML;
            currentLines += lines;
          } else {
            // Need to truncate paragraph content
            const remainingLines = this.MAX_LINES - currentLines;

            if (remainingLines > 0) {
              const truncatedHTML = this.truncateParagraphHTML(
                paragraph,
                remainingLines,
                tempContainer
              );

              if (truncatedHTML) {
                truncatedContent += truncatedHTML;
                currentLines = this.MAX_LINES;
              }
            }

            wasTruncated = true;
          }

          // Clean up tempPara
          tempContainer.removeChild(tempPara);
        }
      }


    }

    // Clean up
    document.body.removeChild(tempContainer);
    if(wasTruncated){
      const sanitize = this.sanitizeStyles(truncatedContent);
      return { content: sanitize, wasTruncated };
    }
    
    return { content: truncatedContent || content, wasTruncated };
  }

  /**
   * Estimates the number of lines for given content
   * @param containerWidth Container width in pixels
   * @param content The content to measure
   * @returns Estimated number of lines
   */
  estimateLines(containerWidth: number, content: string): number {
    if (!containerWidth || !content) return 0;

    const tempContainer = document.createElement("div");
    tempContainer.style.position = "absolute";
    tempContainer.style.visibility = "hidden";
    tempContainer.style.width = `${containerWidth}px`;
    tempContainer.style.fontSize = `${this.FONT_SIZE}px`;
    tempContainer.style.lineHeight = `${this.LINE_HEIGHT}`;
    tempContainer.style.wordWrap = "break-word";
    tempContainer.innerHTML = content;
    document.body.appendChild(tempContainer);

    let totalLines = 0;
    const nodes = Array.from(tempContainer.childNodes);

    for (const node of nodes) {
      if (node.nodeName === "IMG") {
        totalLines += 2;
      } else if (node.nodeName === "TABLE") {
        const table = node as HTMLElement;
        totalLines += this.calculateTableLines(table);
      } else if (node.nodeName === "P") {
        const paragraph = node as HTMLElement;
        const hasImage = paragraph.querySelector("img") !== null;
        const hasTable = paragraph.querySelector("table") !== null;
        const text = paragraph.textContent || "";

        if (hasImage) {
          totalLines += 2;
        } else if (hasTable) {
          const table = paragraph.querySelector("table") as HTMLElement;
          totalLines += this.calculateTableLines(table);
        } else if (!text.trim()) {
          // Empty paragraphs (e.g., <p><br></p>) count as 1 line
          totalLines += 1;
        } else {
          const height = paragraph.offsetHeight;
          totalLines += Math.ceil(height / (this.FONT_SIZE * this.LINE_HEIGHT));
        }
      }
    }

    document.body.removeChild(tempContainer);
    return totalLines;
  }

  /**
   * Calculates the number of lines a table should count as based on its rows
   * @param table The table element to measure
   * @returns Number of lines the table represents
   */
  private calculateTableLines(table: HTMLElement): number {
    if (!table) return 1;

    // Count header rows (thead > tr)
    const theadRows = table.querySelectorAll("thead tr").length;

    // Count body rows (tbody > tr)
    const tbodyRows = table.querySelectorAll("tbody tr").length;

    // Count direct tr children (for tables without thead/tbody)
    const directRows = Array.from(table.children).filter(
      (child) => child.nodeName === "TR"
    ).length;

    // Total rows = thead + tbody, or direct rows if no thead/tbody
    const totalRows = theadRows + tbodyRows || directRows;

    // Each row counts as 1 line, minimum 1 line for empty tables
    return Math.max(1, totalRows);
  }

  /**
   * Removes font-size and text-align styles from HTML content
   * @param html The HTML string to sanitize
   * @returns Sanitized HTML string
   */
  private sanitizeStyles(html: string): string {
    if (!html) return html;

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Remove font-size and text-align from all elements
    const allElements = tempDiv.querySelectorAll("*");
    allElements.forEach((element: Element) => {
      const htmlElement = element as HTMLElement;
      if (htmlElement.style) {
        htmlElement.style.fontSize = "";
        htmlElement.style.textAlign = "";
      }
    });

    return tempDiv.innerHTML;
  }

  /**
   * Truncates paragraph HTML content while preserving formatting
   * @param paragraph The paragraph element to truncate
   * @param maxLines Maximum lines allowed
   * @param container Container for measurement
   * @returns Truncated HTML string
   */
  private truncateParagraphHTML(
    paragraph: HTMLElement,
    maxLines: number,
    container: HTMLElement
  ): string {
    const text = paragraph.textContent || "";
    const words = text.trim().split(/\s+/);

    let tempPara = document.createElement("p");
    tempPara.innerHTML = paragraph.innerHTML;
    container.appendChild(tempPara);

    // Binary search for the right amount of content
    let low = 0;
    let high = words.length;
    let bestFit = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const testWords = words.slice(0, mid).join(" ");

      // Create a test paragraph with truncated text but preserved HTML
      const testPara = this.createTruncatedParagraph(
        paragraph,
        testWords,
        container
      );

      const height = testPara.offsetHeight;
      const lines = Math.ceil(height / (this.FONT_SIZE * this.LINE_HEIGHT));

      container.removeChild(testPara);

      if (lines <= maxLines) {
        bestFit = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    container.removeChild(tempPara);

    if (bestFit === 0) return "";

    const finalWords = words.slice(0, bestFit).join(" ");
    const finalPara = this.createTruncatedParagraph(
      paragraph,
      finalWords + "...",
      container
    );

    const result = finalPara.outerHTML;
    container.removeChild(finalPara);

    return result;
  }

  /**
   * Creates a paragraph with truncated text while preserving HTML formatting
   * @param originalParagraph Original paragraph element
   * @param truncatedText The truncated text content
   * @param container Container for temporary elements
   * @returns Paragraph element with preserved formatting
   */
  private createTruncatedParagraph(
    originalParagraph: HTMLElement,
    truncatedText: string,
    container: HTMLElement
  ): HTMLElement {
    const newPara = document.createElement("p");
    newPara.innerHTML = originalParagraph.innerHTML;

    // Walk through text nodes and truncate
    this.truncateTextNodes(newPara, truncatedText);

    container.appendChild(newPara);
    return newPara;
  }

  /**
   * Truncates text nodes within an element while preserving structure
   * @param element The element to process
   * @param targetText The target text length to achieve
   */
  private truncateTextNodes(element: HTMLElement, targetText: string): void {
    let currentLength = 0;
    const targetLength = targetText.length;

    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: Text[] = [];
    let node: Node | null;

    while ((node = walker.nextNode())) {
      textNodes.push(node as Text);
    }

    for (const textNode of textNodes) {
      const nodeText = textNode.textContent || "";
      const nodeLength = nodeText.length;

      if (currentLength + nodeLength <= targetLength) {
        currentLength += nodeLength;
      } else {
        const remainingLength = targetLength - currentLength;
        textNode.textContent = nodeText.substring(0, remainingLength);
        currentLength = targetLength;

        // Remove all following text nodes
        const index = textNodes.indexOf(textNode);
        for (let i = index + 1; i < textNodes.length; i++) {
          textNodes[i].textContent = "";
        }
        break;
      }
    }
  }
}
